// Angular imports
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';

// Import services

// Modal system imports
import { BaseModalComponent } from '../../modal/base-modal';
import { ModalService } from '../../modal/modal.service';

// Import types
import { Project } from '../../types/project';
import { CalculationApi, CalculationHeaderApi, CalculationLineApi, CalculationMetaApi, TemplateCategoryApi } from '../../types/calculation';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, take, takeUntil, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import * as CalculationActions from '../../state-management/calculation-actions';
import * as HeaderActions from '../../state-management/actions/header-line-actions';
import { selectCalculationMeta, selectHourRates, selectTemplate } from 'src/app/state-management/calculation-selectors';
import { AppState } from 'src/app/state-management/app-state';
import { Actions, ofType } from '@ngrx/effects';
import { CreateLineService } from 'src/app/services/create-line/create-line.service';
import { ProjectCalculationService } from 'src/app/services/project-calculation/project-calculation.service';
import { MatSelectModule } from '@angular/material/select';

// Declare types
type CategoryView = {
    code: string;
    description: string;
    subCategories: SubcategoryView[]

    open: boolean;
    selected: boolean;
};

type SubcategoryView = {
    code: string;
    description: string;
    selected: boolean;
};

declare type NestedCalculationApi = CalculationApi & {
    children?: NestedCalculationApi[];
};

// Constants
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
    selector: 'import-lines-modal',
    templateUrl: './import-lines-modal.component.html',
    styleUrls: ['./import-lines-modal.component.scss'],
    standalone: false,
})
export class ImportLinesModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
    private unsubscribe$ = new Subject<void>()

    @ViewChild('search') public _searchField: ElementRef<HTMLInputElement>;

    // Title for modal
    public title = 'Kopiëren vanaf calculatie';

    // State
    public selectingCalculation = true;

    // Selecting
    public searchBarSelected = false;
    public hasSelection = false;

    // Copy
    public selectedRefProject: Project = null;
    public selectedRefCalculation: CalculationApi = null;
    public selectedRefMeta$ = new BehaviorSubject<CalculationMetaApi>(undefined);
    public categoryViews: CategoryView[] = [];
    public hasTail: boolean = false;
    public allSelected = false;
    public loadingCategories = false;
    public importTail = false;
    public importHourlyRates = true;

    // Filters
    public projFilterActive$ = new BehaviorSubject<boolean>(true);
    public projFilterClosed$ = new BehaviorSubject<boolean>(false);
    private filterText$ = new BehaviorSubject<string>('');

    public loading = false;

    // The template of the current calculation
    template$ = new BehaviorSubject<TemplateCategoryApi[]>(undefined);

    // The meta info for the active calculation
    meta$ = new BehaviorSubject<CalculationMetaApi>(undefined);

    // Helper to build the version list
    createRefVersions(n: number) {
        // Create [1..n] with labels
        return Array.from({ length: n }, (_, i) => ({
            version: i + 1,
            label: `Versie ${i + 1}`,
        }))
    }

    // Derived stream (cache last emission, emit [] initially)
    readonly versions$ = this.selectedRefMeta$.pipe(
        filter(meta => !!meta),
        tap(meta => console.log('version', meta)),
        take(1),
        map(meta => this.createRefVersions(meta?.currentVersion ?? 1)),
        startWith([] as { version: number; label: string }[]),
        shareReplay({ bufferSize: 1, refCount: true })
    )

    selectedVersion: number = 1;

    // The hourly rates used in the active calculation
    hourlyRates$ = new BehaviorSubject<CalculationHeaderApi[]>([]);

    // Projects
    public filteredProjects$ = combineLatest([
        this.projectCalculationService.projectList$.pipe(
            map(projects => projects.filter(p => p.calculations?.length > 0)),
        ),
        this.projFilterActive$.pipe(startWith(true)),
        this.projFilterClosed$.pipe(startWith(false)),
        this.filterText$.pipe(
            startWith(''),
            debounceTime(200)
        )
    ])
        .pipe(
            distinctUntilChanged(),
            map(([projects, active, closed, filter]) =>
                projects
                    .filter(p =>
                        // If active and closed are both false ignore them
                        ((!active && !closed && p.active) || ((active && p.active) || (closed && !p.active))) &&
                        // Filter on search text. Split up on spaces and match every word
                        filter.split(' ').every(e => p.id.includes(e) || p.description?.toLowerCase().includes(e))
                    )
                    .map(p => ({
                        ...p,
                        calculations: this.buildCalculationTree(p.calculations ?? [])
                    }))
            )
        );

    public loadingProjects = false;

    public filterOpenProjectsOnly: boolean = true;
    private importing = false;

    constructor(
        private modalService: ModalService,
        private store: Store<AppState>,
        private actions$: Actions,
        private createLineService: CreateLineService,
        private projectCalculationService: ProjectCalculationService
    ) {
        super();

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta$.next(meta);
        })

        this.store.select(selectTemplate).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(template => {
            this.template$.next(template);
        })

        this.store.select(selectHourRates).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(rates => this.hourlyRates$.next(rates))

        this.actions$.pipe(
            takeUntil(this.unsubscribe$),
            ofType(CalculationActions.loadRefCalculationSuccess)
        ).subscribe(action => {
            if (!this.loadingCategories) {
                return;
            }
            this.selectedRefCalculation = action.calculation;
            this.selectedRefMeta$.next(action.calculation.meta);
            this.handleRefCalculationLoad();
        })

        this.filteredProjects$.subscribe(projects => {
            this.loadingProjects = false;
        });
    }

    ngOnInit() {
        this.selectedRefMeta$.subscribe(meta => {
            if (meta) {
                console.log('Meta changed', meta);
                this.selectedVersion = meta.currentVersion;
            }
        });
    }

    // TODO: move to service, since exact same logic is used in multiple places (import lines)
    private buildCalculationTree(calculations: NestedCalculationApi[]): NestedCalculationApi[] {
        // Function to recursively build the tree structure
        const map = new Map<string, NestedCalculationApi>();
        const roots: NestedCalculationApi[] = [];

        calculations.forEach(calc => {
            map.set(calc.meta.id, { ...calc, children: [] });
        });

        calculations.forEach(calc => {
            if (calc.meta.parentCalculationId) {
                const parent = map.get(calc.meta.parentCalculationId);
                if (parent) {
                    parent.children.push(map.get(calc.meta.id)!);
                }
            } else {
                roots.push(map.get(calc.meta.id)!);
            }
        });

        return roots;
    }

    public async init() {
        if (!this.meta$.value) {
            console.error('Cannot import lines when active calculation is not set');
            this.modalService.close();
        }

        this.loading = true;
    }

    public async importSelection() {
        if (!this.hasSelection && !this.importTail) return;
        if (this.importing) return;
        if (!this.template$.value || !this.meta$.value) {
            return;
        }

        // Set importing
        this.importing = true;

        let ratesDict: { [oldRateId: string]: string } = (this.selectedRefCalculation.header ?? []).reduce((p, c) => {
            // Map by name
            p[c.id] = this.hourlyRates$.value.find(f => f.description === c.description)?.id ?? EMPTY_GUID;   // Default to standard rate

            return p;
        }, {});

        // Copy rates befor lines
        if (this.importHourlyRates) {
            ratesDict = this.copyHourRates();
        }

        console.log('RATE DICT', ratesDict);

        const lines: CalculationLineApi[] = [];
        this.categoryViews.forEach(catView => {
            // Get the category
            const category = this.selectedRefCalculation.categories.find(c => c.code === catView.code);

            catView.subCategories.forEach(subCatView => {
                if (!subCatView.selected) return;

                // Get the sub-category
                const subCategory = category.subCategories.find(s => s.code === subCatView.code);

                lines.push(...subCategory.lines.map(m => ({
                    ...m,
                    rate: ratesDict[m.rate] ?? EMPTY_GUID
                })));
            });
        });

        // Create the lines
        if (lines.length > 0) {
            await this.createLineService.createAndInsertCalculationLines(lines.map(l => ({
                ...l,
                id: undefined,
                sortOrder: undefined
            })), true, 'create');
        }

        // Create the tail lines
        if (this.importTail) {
            // Make copy
            const tailCopy = this.selectedRefCalculation?.tail?.map(m => ({ ...m })) ?? []
            // Sort before importing
            tailCopy.sort((t1, t2) => (t1?.sortOrder ?? 0) - (t2?.sortOrder ?? 0));

            await this.createLineService.createAndInsertTailLines(tailCopy, true, 'create');
        }

        // Clear importing
        this.importing = false;

        // Trigger close
        this.modalService.close();
    }

    private copyHourRates(): { [oldRateId: string]: string } {
        const result: { [oldRateId: string]: string } = {};

        const projectId = this.meta$.value.projectId;
        const calculationId = this.meta$.value.id;

        if (!projectId || !calculationId) {
            throw new Error('Missing project or calculation id');
        }

        const existingRates = this.hourlyRates$.value;

        // Get the rates of the copied calculation
        const copyRates = this.selectedRefCalculation.header ?? [];

        // Update existing
        const updateRates = existingRates.filter(f => copyRates.find(fnd => fnd.description === f.description));
        const addRates = copyRates.filter(f => !updateRates.find(fnd => fnd.description === f.description));

        console.log('UPDATE RATES', updateRates);
        console.log('ADD RATES', addRates);

        // TODO: Bulk update and delete header lines
        updateRates.forEach(target => {
            // Get corresponding item in reference calculation
            const source = copyRates.find(f => f.description === target.description);

            // Enter in dictionary
            result[source.id] = target.id;

            // Only updating amount
            this.store.dispatch(HeaderActions.updateHeaderLine({
                projectId,
                calculationId,
                line: {
                    ...target,      // Keep all values from active calculation rate
                    value: source.value  // Update rate with value from copied calculation
                }
            }));
        })

        addRates.forEach(e => {
            // Adding with same id, so no mapping
            result[e.id] = e.id;

            // Only updating amount
            this.store.dispatch(HeaderActions.addHeaderLine({
                projectId,
                calculationId,
                rate: {
                    ...e
                }
            }));
        })

        // Return the new list of rates, so copied lines can be matched on rate by description
        return result;
    }

    /* --- Event handlers --- */

    public async selectCalculation(project: Project, calculation: CalculationApi) {
        // Set selected project
        this.selectedRefProject = project;

        // Mark as no longer selecting
        this.selectingCalculation = false;

        // Load categories and set selected calculation
        this.loadingCategories = true;

        // this.selectedCalculation = await this.dataService.loadCalculation(project, calculation);
        this.store.dispatch(CalculationActions.loadRefCalculation({
            projectId: project.id,
            calculationId: calculation.meta.id
        }));
    }

    public async versionChange($event: Event) {
        // Should have enough data to collect calculation version
        if(!this.selectedRefProject || !this.selectedRefMeta$.value) {
            return;
        }

        const selectElement = $event.target as HTMLSelectElement;
        this.selectedVersion = Number(selectElement.value);

        // Need to reset flag:
        this.loadingCategories = true;
        // And the currenct view
        this.categoryViews = [];

        // Use the meta data for project and calculation
        const { projectId, id: calculationId } = this.selectedRefMeta$.value;
        // Use the selected version
        this.store.dispatch(CalculationActions.loadRefCalculation({
            projectId,
            calculationId,
            version: this.selectedVersion
        }));
    }

    public handleRefCalculationLoad() {
        this.loadingCategories = false;

        if (!this.selectedRefCalculation) {
            return;
        }

        // Set has tail
        this.hasTail = !!this.selectedRefCalculation.tail && this.selectedRefCalculation.tail.length > 0;

        // Create a dictionary for the category names
        const templateDict = this.selectedRefCalculation.template.reduce<{ [code: string]: string }>((p, c) => {
            p[c.categoryCode] = c.categoryName;

            for (const sub of c.subcategories) {
                p[sub.subcategoryCode] = sub.subcategoryName
            }
            return p;
        }, {});

        // Map category views
        this.categoryViews = this.selectedRefCalculation.categories.map(cat => {
            return {
                code: cat.code,
                description: templateDict[cat.code],
                open: false,
                selected: false,

                subCategories: cat.subCategories.map(subCat => {
                    return {
                        code: subCat.code,
                        description: templateDict[subCat.code],
                        selected: false
                    }
                })
            }
        });
    }

    public clickSelectEverything(event: Event) {
        // Stop further event propagation
        event.stopPropagation();

        // Toggle all selected
        this.allSelected = !this.allSelected;

        // Effect all categories
        this.categoryViews.forEach(cv => {
            cv.selected = this.allSelected;
            cv.open = true;

            cv.subCategories.forEach(scv => {
                scv.selected = this.allSelected;
            });
        });

        // Set has selection
        this.hasSelection = this.allSelected;
    }

    public clickCategoryCheckbox(event: Event, category: CategoryView) {
        // Stop further event propagation
        event.stopPropagation();

        // Toggle category selection and set category to open
        category.selected = !category.selected;
        category.open = true;

        // Effect all sub categories
        category.subCategories.forEach(s => s.selected = category.selected);

        // Effect all selected
        this.allSelected = this.categoryViews.find(cv => !cv.selected) === undefined;

        // Check if there is a selection
        this.hasSelection = this.categoryViews.find(cv => {
            return cv.subCategories.find(scv => {
                return scv.selected
            }) !== undefined
        }) !== undefined;
    }

    public clickSubCategoryCheckbox(event: Event, category: CategoryView, subCategory: SubcategoryView) {
        // Stop further event propagation
        event.stopPropagation();

        // Toggle sub-category selection
        subCategory.selected = !subCategory.selected;

        // Effect the categroy
        const allSelectedInCategory = category.subCategories.find(s => !s.selected) === undefined;
        category.selected = allSelectedInCategory;

        // Effect all selected
        this.allSelected = this.categoryViews.find(cv => !cv.selected) === undefined;

        // Check if there is a selection
        this.hasSelection = this.categoryViews.find(cv => {
            return cv.subCategories.find(scv => {
                return scv.selected
            }) !== undefined
        }) !== undefined;
    }

    public searchInputChange(search: string) {
        this.filterText$.next(search.toLowerCase());
    }

    public focusSearchBar() {
        this.searchBarSelected = true;
    }

    public blurSearchBar() {
        this.searchBarSelected = false;
    }

    public toggleFilterValue(filter: string) {
        switch (filter) {
            case 'isActive':
                this.projFilterActive$.next(!this.projFilterActive$.value);
                break;
            case 'isClosed':
                this.projFilterClosed$.next(!this.projFilterClosed$.value);
                break;
        }
    }

    public ngAfterViewInit(): void {
        // Set focus to prevend typing in the underlying calculation
        this._searchField?.nativeElement?.focus();
    }
}
